import { useCallback, useMemo } from 'react';
import {
  makeStyles,
  mergeClasses,
  tokens,
  Tooltip,
  Text,
} from '@fluentui/react-components';
import { Info16Regular } from '@fluentui/react-icons';
import type { OperationDefinition } from '@/types/operations';

/**
 * Stable class name used as a selector target by parent rows that want to
 * reveal the info icon on `:hover` / `:focus-within`. Kept as a literal string
 * so parent styles can reference it without makeStyles hash mangling.
 */
export const OPERATION_INFO_REVEAL_CLASS = 'otto-op-info';

const useStyles = makeStyles({
  trigger: {
    flexShrink: 0,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '24px',
    height: '24px',
    borderRadius: tokens.borderRadiusSmall,
    color: tokens.colorNeutralForeground3,
    backgroundColor: 'transparent',
    cursor: 'help',
    fontSize: tokens.fontSizeBase400,
    // Hidden by default; the parent row reveals it on hover / focus-within.
    opacity: 0,
    transitionProperty: 'opacity, background-color, color',
    transitionDuration: tokens.durationFast,
    transitionTimingFunction: tokens.curveEasyEase,
    ':hover': {
      backgroundColor: tokens.colorNeutralBackground1Hover,
      color: tokens.colorNeutralForeground1,
      opacity: 1,
    },
    ':focus-visible': {
      opacity: 1,
      outline: `2px solid ${tokens.colorBrandStroke1}`,
      outlineOffset: '1px',
    },
    '@media (prefers-reduced-motion: reduce)': {
      transitionDuration: '0.01ms',
    },
    '@media (forced-colors: active)': {
      color: 'CanvasText',
    },
  },
  surface: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
    maxWidth: '320px',
  },
  header: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXXS,
  },
  name: {
    fontSize: tokens.fontSizeBase300,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
    wordBreak: 'break-word',
    lineHeight: tokens.lineHeightBase300,
  },
  connector: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
    wordBreak: 'break-word',
  },
  description: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground2,
    wordBreak: 'break-word',
    lineHeight: tokens.lineHeightBase200,
    whiteSpace: 'pre-wrap',
  },
  emptyDescription: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
    fontStyle: 'italic',
  },
});

type OperationInfoButtonProps = {
  operation: OperationDefinition;
  /**
   * Override the tooltip positioning. Defaults to `above` so the surface
   * doesn't push list rows around.
   */
  positioning?: 'above' | 'after' | 'before' | 'below';
};

/**
 * Info icon shown on hover for an operation row. Opens a tooltip with the
 * operation's full name, connector, and description so users can read
 * truncated text or learn more about what an operation does before adding it.
 *
 * Uses Fluent's `Tooltip` so it opens on both hover and keyboard focus,
 * and closes on `Escape` / blur / mouse leave automatically. Rendered as
 * `<span role="button">` so it can be nested inside another `<button>`
 * (operation row) without violating HTML interactive-content rules.
 */
export const OperationInfoButton = ({
  operation,
  positioning = 'above',
}: OperationInfoButtonProps) => {
  const styles = useStyles();

  // Stop click/key events from bubbling up to the surrounding operation row
  // button (which would otherwise add the operation to the workflow).
  const stopRowActivation = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
  }, []);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      e.stopPropagation();
    }
  }, []);

  const description = operation.description?.trim();

  const content = useMemo(
    () => (
      <div className={styles.surface}>
        <div className={styles.header}>
          <Text as="h4" className={styles.name}>
            {operation.name}
          </Text>
          {operation.connectorName && (
            <Text className={styles.connector}>{operation.connectorName}</Text>
          )}
        </div>
        {description ? (
          <Text className={styles.description}>{description}</Text>
        ) : (
          <Text className={styles.emptyDescription}>
            No description provided.
          </Text>
        )}
      </div>
    ),
    [
      styles,
      operation.name,
      operation.connectorName,
      description,
    ],
  );

  return (
    <Tooltip
      content={content}
      relationship="description"
      withArrow
      positioning={positioning}
    >
      <span
        role="button"
        tabIndex={0}
        className={mergeClasses(OPERATION_INFO_REVEAL_CLASS, styles.trigger)}
        onClick={stopRowActivation}
        onKeyDown={handleKeyDown}
        aria-label={`Show details for ${operation.name}`}
        title="Show details"
      >
        <Info16Regular />
      </span>
    </Tooltip>
  );
};

export default OperationInfoButton;
